import type { CSSProperties, ReactElement } from 'react';

/**
 * Bedrock `<Backlight>` — a soft, brand-tinted pool of light behind a demo.
 *
 * ## What it is for
 *
 * The landing page's two demos are white product surfaces on a dark ground:
 * the story stage floats six white cards plus a chat widget on `--bd-band-black`
 * (pure `#000`), and how-it-works floats one large white panel on `--bd-band`.
 * Composited flat, those surfaces do not read as sitting IN the band — they
 * read as holes punched through it, because nothing in the picture explains
 * where the light on them comes from. The band-level wash `Band` already paints
 * does not fix that: it is deliberately positioned at the band's EDGES (top,
 * far left, far right) as chrome, so it leaves the middle — which is exactly
 * where the components are — at the flat ground colour.
 *
 * This is the missing middle. It sits BEHIND the components, spreads wider than
 * they do, and lifts the ground under them a few percent toward the brand ramp.
 * The white then reads as lit rather than as cut out.
 *
 * ## Why one component and not a gradient per band
 *
 * Two call sites already want it, and a backlight is the kind of decoration
 * that gets copied and then retuned in one place only — after which two demos
 * on one page glow in two different colours and nobody can say which is
 * correct. `Band` splits `BandAmbient` out for the same reason: this file reads
 * as paint, the caller reads as layout.
 *
 * ## It drifts, and the channel it drifts in is the whole trick
 *
 * The band wash (`bdGlow`) pulses OPACITY. A backlight that pulsed opacity too
 * would sum with it into a beat at a period neither author chose — an irregular
 * throb rather than ambience — which is why this was static at first. Moving on
 * TRANSFORM instead sidesteps that completely: two different channels
 * composite cleanly at any period, so the pool can breathe under a band that is
 * already breathing. It also stays on the compositor, where a very large
 * gradient layer belongs.
 *
 * The travel is a couple of percent plus a small swell, over 19s. The subject
 * does not move, so motion a visitor can TRACK reads as the light sliding off
 * whatever it is lighting; the target is "alive when you look away", not
 * "animated". `data-bd-anim` hooks the global `prefers-reduced-motion` switch
 * in `bedrock.css`, so a visitor who asked for less motion gets the pool
 * holding still rather than gone.
 *
 * ## It is decoration, and stays decoration
 *
 * `aria-hidden` and `pointer-events: none`, always — not props. A layer that
 * spreads past its subject on every side would otherwise swallow clicks on
 * whatever sits near it, and it has nothing to say to a screen reader. The
 * story stage in particular puts this behind an `inert` mock; a backlight that
 * could take a click would be a hazard there rather than a nuisance.
 */

export interface BacklightProps {
  /**
   * Position + size. The caller owns geometry because only it knows where its
   * components sit; this file owns colour. Default fills the nearest positioned
   * ancestor, which is right when the subject is centred in it.
   */
  readonly className?: string;
  readonly style?: CSSProperties;
}

/**
 * Three pools, ramp-ordered left to right — cyan, blue, violet.
 *
 * The blue is the widest and the strongest because it is the ramp's centre and
 * the colour the mark reads as; the other two are roughly half its strength and
 * only shade the falloff, so the result is a tint rather than a colour. Alpha
 * caps at `.23`, which over `#000` lifts the ground to about `#0a1740` at the
 * core — clearly "not black", and still far enough from a white card that the
 * card keeps every bit of the attention.
 *
 * `rgb(var(--bd-*-rgb) / a)` rather than a hex, so the ramp stays in one place;
 * the `-rgb` companions exist for exactly this (see `bedrock.css`) and are
 * pinned to their hexes by `brand-ramp.test.ts`.
 */
const POOLS = [
  'radial-gradient(58% 54% at 50% 48%, rgb(var(--bd-blue-rgb) / 0.23), transparent 72%)',
  'radial-gradient(46% 46% at 12% 58%, rgb(var(--bd-cyan-rgb) / 0.11), transparent 74%)',
  'radial-gradient(48% 48% at 88% 40%, rgb(var(--bd-violet-rgb) / 0.12), transparent 74%)',
].join(',');

/**
 * Slow enough that the movement is never the thing you notice — see the drift
 * note above. Coprime-ish with the 12s/13s band washes so the two do not keep
 * re-meeting at the same phase.
 */
const DRIFT = 'bdBacklight 19s ease-in-out infinite';

export function Backlight({ className, style }: BacklightProps): ReactElement {
  return (
    <div
      aria-hidden="true"
      data-bd-backlight
      data-bd-anim
      className={['pointer-events-none absolute', className ?? 'inset-0'].join(' ')}
      style={{ background: POOLS, animation: DRIFT, ...style }}
    />
  );
}
